import { useCallback, useEffect, useState } from 'react';
import { Search, Sparkles, AlertCircle, Loader2, UserPlus, UserMinus, Globe, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';
import { parseAdminStaffSession } from '../utils/adminSession';
import {
  superadminGrantCreatorRole,
  superadminRevokeCreatorRole,
} from '../utils/creatorApi';
import type { AppUserRole } from '../types/roles';

interface UserRow {
  hy_user_id: string;
  hy_name: string;
  email: string | null;
  role: AppUserRole;
  creator_handle: string | null;
  credit_balance: number;
  updated_at: string;
}

type Tab = 'creators' | 'eligible';

export function CreatorsPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [tab, setTab] = useState<Tab>('creators');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [grantTarget, setGrantTarget] = useState<UserRow | null>(null);
  const [grantHandle, setGrantHandle] = useState('');
  const [granting, setGranting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const staff = parseAdminStaffSession();
  const isSuperadmin = staff?.role === 'superadmin';

  const load = useCallback(async () => {
    if (!isSuperadmin) return;
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('users')
      .select('hy_user_id, hy_name, email, role, creator_handle, credit_balance, updated_at')
      .order('updated_at', { ascending: false })
      .limit(500);
    if (e) {
      setError(e.message);
    } else {
      setUsers((data ?? []) as UserRow[]);
    }
    setLoading(false);
  }, [isSuperadmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = users.filter((u) => {
    if (tab === 'creators' && u.role !== 'creator' && u.role !== 'admin' && u.role !== 'superadmin') {
      return false;
    }
    if (tab === 'eligible' && u.role !== 'user') return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      u.hy_name.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      u.hy_user_id.toLowerCase().includes(q) ||
      (u.creator_handle || '').toLowerCase().includes(q)
    );
  });

  const handleGrant = async () => {
    if (!grantTarget) return;
    const handleVal = grantHandle.trim() || null;
    setGranting(true);
    setError(null);
    const result = await superadminGrantCreatorRole({
      targetHyUserId: grantTarget.hy_user_id,
      handle: handleVal,
    });
    setGranting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(
      `${grantTarget.hy_name} is now a creator${
        result.data.creator_handle ? ` (@${result.data.creator_handle})` : ''
      }.`
    );
    setGrantTarget(null);
    setGrantHandle('');
    void load();
  };

  const handleRevoke = async (u: UserRow) => {
    if (!window.confirm(`Revoke creator role from ${u.hy_name}?`)) return;
    setRevokingId(u.hy_user_id);
    setError(null);
    const result = await superadminRevokeCreatorRole({ targetHyUserId: u.hy_user_id });
    setRevokingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(`${u.hy_name} is back to user role.`);
    void load();
  };

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <p className="text-th-secondary max-w-md">
          Creator management is limited to superadmin accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        title="Creators"
        subtitle="Grant the creator role and assign public handles"
      >
        <button
          type="button"
          className="button-secondary text-sm inline-flex items-center gap-1.5"
          onClick={() => navigate('/admin/creators/queue')}
        >
          Review queue
        </button>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-y-auto max-w-6xl mx-auto w-full px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-th-secondary" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, id, or handle\u2026"
              className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-lg text-th-primary"
            />
          </div>
          <Filter className="w-4 h-4 text-th-secondary shrink-0" />
          <button
            type="button"
            onClick={() => setTab('creators')}
            className={`px-3 py-1.5 rounded-full text-sm ${
              tab === 'creators' ? 'bg-brand text-surface' : 'bg-elevated border border-subtle text-th-secondary'
            }`}
          >
            Creators ({users.filter((u) => u.role === 'creator' || u.role === 'admin' || u.role === 'superadmin').length})
          </button>
          <button
            type="button"
            onClick={() => setTab('eligible')}
            className={`px-3 py-1.5 rounded-full text-sm ${
              tab === 'eligible' ? 'bg-brand text-surface' : 'bg-elevated border border-subtle text-th-secondary'
            }`}
          >
            Eligible users ({users.filter((u) => u.role === 'user').length})
          </button>
        </div>

        {success && (
          <div className="p-3 rounded-lg border border-status-success/30 bg-status-success/10 text-status-success text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            {success}
            <button type="button" className="ml-auto text-xs underline" onClick={() => setSuccess(null)}>
              Dismiss
            </button>
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg border border-status-error/30 bg-status-error/10 text-status-error text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-th-secondary text-sm py-8 text-center">No matching users.</p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((u) => (
              <li key={u.hy_user_id} className="card p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-th-primary truncate">{u.hy_name}</h3>
                    <RoleBadge role={u.role} />
                    {u.creator_handle && (
                      <a
                        href={`/c/${u.creator_handle}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-2 py-0.5 rounded-full border border-subtle bg-elevated text-th-secondary hover:text-th-primary inline-flex items-center gap-1"
                      >
                        <Globe className="w-3 h-3" />@{u.creator_handle}
                      </a>
                    )}
                  </div>
                  <p className="text-xs text-th-tertiary mt-1 font-mono truncate">{u.hy_user_id}</p>
                  {u.email && <p className="text-xs text-th-tertiary truncate">{u.email}</p>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {u.role === 'user' ? (
                    <button
                      type="button"
                      className="button-primary text-sm inline-flex items-center gap-1.5"
                      onClick={() => {
                        setGrantTarget(u);
                        setGrantHandle('');
                      }}
                    >
                      <UserPlus className="w-4 h-4" />
                      Grant creator
                    </button>
                  ) : u.role === 'creator' ? (
                    <button
                      type="button"
                      disabled={revokingId === u.hy_user_id}
                      className="button-secondary text-sm inline-flex items-center gap-1.5"
                      onClick={() => void handleRevoke(u)}
                    >
                      {revokingId === u.hy_user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <UserMinus className="w-4 h-4" />
                      )}
                      Revoke
                    </button>
                  ) : (
                    <span className="text-xs text-th-tertiary self-center">
                      Staff role \u2014 manage in Users
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {grantTarget && (
        <div className="fixed inset-0 bg-black/80 z-modal-content flex items-center justify-center p-4">
          <div className="bg-surface border border-subtle rounded-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-bold text-th-primary">Grant creator \u2014 {grantTarget.hy_name}</h2>
            <p className="text-xs text-th-secondary font-mono break-all">{grantTarget.hy_user_id}</p>
            <label className="block space-y-1">
              <span className="text-sm text-th-secondary">Public handle (optional)</span>
              <input
                type="text"
                value={grantHandle}
                onChange={(e) => setGrantHandle(e.target.value)}
                placeholder="lowercase, 3-32 chars (a-z 0-9 _ . -)"
                className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary font-mono"
              />
              <span className="text-xs text-th-tertiary">
                Leave blank to assign later via this page or the Users tab.
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="button-secondary" onClick={() => setGrantTarget(null)} disabled={granting}>
                Cancel
              </button>
              <button type="button" className="button-primary" onClick={() => void handleGrant()} disabled={granting}>
                {granting ? 'Granting\u2026' : 'Grant role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: AppUserRole }) {
  if (role === 'superadmin')
    return (
      <span className="text-xs px-2 py-0.5 rounded-full border text-status-error bg-status-error/10 border-status-error/30">
        Superadmin
      </span>
    );
  if (role === 'admin')
    return (
      <span className="text-xs px-2 py-0.5 rounded-full border text-status-warning bg-status-warning/10 border-status-warning/30">
        Admin
      </span>
    );
  if (role === 'creator')
    return (
      <span className="text-xs px-2 py-0.5 rounded-full border text-brand bg-brand/10 border-brand/30">
        Creator
      </span>
    );
  return (
    <span className="text-xs px-2 py-0.5 rounded-full border border-subtle bg-elevated text-th-secondary">
      User
    </span>
  );
}
